import { useEffect, useState } from "react";
import { Contact } from "../lib/api";
import { research, type Sender } from "../lib/research";
import { outreachFetch, useBusiness, DEFAULT_BUSINESS_ID } from "../lib/business";
import SendScheduleControls from "./SendScheduleControls";

interface Campaign {
  id: number; name: string; subject: string; status: string;
  from_email?: string | null; from_name?: string | null; reply_to?: string | null;
  sent_count: number; sent_ok: number; sent_failed: number;
  created_at: string;
}
interface Props {
  selected: Contact[];     // the rows user ticked on the contacts table
  onClose: () => void;
  onSent: (queued: number, scheduledAt: string | null) => void;
}

export default function SendCampaignModal({ selected, onClose, onSent }: Props) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [pickedId, setPickedId]   = useState<number | null>(null);
  const [loading, setLoading]     = useState(true);
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [done, setDone]           = useState<{ queued: number; scheduledAt: string | null } | null>(null);
  const [senders, setSenders]     = useState<Sender[]>([]);
  const [senderId, setSenderId]   = useState<number | null>(null);   // null = the template's own from-address
  const { current: business, id: businessId } = useBusiness();
  // Only e-Veritas has a sending account configured outside Senders.
  const needsSender = businessId !== DEFAULT_BUSINESS_ID;

  useEffect(() => {
    outreachFetch("/api/outreach/campaigns")
      .then(r => r.json() as Promise<{ campaigns?: Campaign[] }>)
      .then(d => {
        setCampaigns(d.campaigns || []);
        if (d.campaigns && d.campaigns.length === 1) setPickedId(d.campaigns[0].id);
      })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
    research.senders()
      .then(d => {
        const active = d.senders.filter(s => !s.disabled);
        setSenders(active);
        if (active.length >= 1 && (active.length === 1 || businessId !== DEFAULT_BUSINESS_ID)) setSenderId(active[0].id);
      })
      .catch(() => { /* no sender accounts — the template's from-address is used */ });
  }, []);

  const sendable = selected.filter(c => c.email);
  const skipped  = selected.length - sendable.length;

  async function send(sendAt: string | null) {
    if (!pickedId) { setError("Pick a campaign first."); return; }
    setBusy(true); setError(null);
    // Safety net: never let the button spin forever if the network stalls.
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 30000);
    try {
      const r = await outreachFetch(`/api/outreach/campaigns/${pickedId}/send`, {
        method: "POST", signal: ctrl.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ids: sendable.map(c => c.id),
          only_valid: false,    // user picked these rows explicitly — don't filter by verification
          allow_resend: true,   // explicit hand-pick → send even if they already got it
          ...(senderId ? { sender_id: senderId } : {}),
          ...(sendAt ? { send_at: sendAt } : {}),
        }),
      });
      const d = (await r.json()) as { ok?: boolean; queued?: number; scheduled_at?: string | null; error?: string };
      if (!r.ok) { setError(d.error || "Send failed"); return; }
      // Show a non-blocking success panel; the parent refreshes when the user closes.
      setDone({ queued: d.queued || 0, scheduledAt: d.scheduled_at ?? null });
    } catch (e) {
      setError(ctrl.signal.aborted
        ? "Timed out waiting for the server. It may still have been scheduled — check Outreach → Email log."
        : (e instanceof Error ? e.message : String(e)));
    } finally { clearTimeout(timer); setBusy(false); }
  }

  function fmtWhen(s: string): string {
    return new Date(s.replace(" ", "T") + "Z").toLocaleString();
  }

  const picked = campaigns.find(c => c.id === pickedId);
  const sender = senders.find(s => s.id === senderId);
  const unverified = sendable.filter(c => c.verification_status !== "valid").length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onMouseDown={(e) => { if (e.button === 0 && e.target === e.currentTarget) onClose(); }}
    >
      <div className="card w-full max-w-2xl p-5 shadow-xl">
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold tracking-tightish text-slate-900">Send email</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {selected.length} contact{selected.length === 1 ? "" : "s"} selected · {sendable.length} have an email
              {skipped > 0 && <span className="text-amber-700"> · {skipped} will be skipped (no email)</span>}
            </p>
          </div>
          <button className="btn-ghost !text-xs !py-1 !px-2" onClick={onClose}>Close</button>
        </div>

        {error && <div className="mb-3 rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

        {done && (
          <div className="space-y-4">
            <div className="rounded-md bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
              <div className="text-base">✓ {done.queued} email{done.queued === 1 ? "" : "s"} {done.scheduledAt ? "scheduled" : "queued"}</div>
              <div className="mt-1 text-emerald-700">
                {done.scheduledAt
                  ? <>Will send automatically on <strong>{fmtWhen(done.scheduledAt)}</strong>.</>
                  : <>Sending now in the background.</>}
                {done.queued === 0 && <> No new recipients — already-sent contacts are skipped.</>}
              </div>
            </div>
            <div className="flex justify-end">
              <button className="btn-primary !text-xs !py-1.5 !px-3"
                      onClick={() => onSent(done.queued, done.scheduledAt)}>Done</button>
            </div>
          </div>
        )}

        {!done && loading && <div className="py-6 text-center text-sm text-slate-500">Loading campaigns…</div>}

        {!done && !loading && campaigns.length === 0 && (
          <div className="rounded bg-amber-50 px-3 py-3 text-sm text-amber-800">
            No saved templates yet. Create one first at <a className="underline" href="/outreach/campaigns/new">Outreach → Templates → New</a>.
          </div>
        )}

        {!done && !loading && campaigns.length > 0 && (
          <>
            <div className="mb-1 text-xs font-medium text-slate-600">Template</div>
            <div className="max-h-72 overflow-y-auto rounded-md border border-slate-200">
              {campaigns.map(c => (
                <label key={c.id}
                       className={`flex cursor-pointer items-start gap-3 border-b border-slate-100 px-3 py-2.5 last:border-b-0 hover:bg-slate-50 ${pickedId === c.id ? "bg-indigo-50/60" : ""}`}>
                  <input type="radio" name="campaign" className="mt-0.5"
                         checked={pickedId === c.id}
                         onChange={() => setPickedId(c.id)} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="truncate text-sm font-medium text-slate-900">{c.name}</div>
                      <span className="pill pill-slate text-[10px]">{c.status}</span>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-slate-500">{c.subject}</div>
                    <div className="mt-0.5 text-[10.5px] text-slate-400">
                      From {c.from_email || "(default)"} · {c.sent_ok} sent / {c.sent_failed} failed
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="mt-3">
              <label className="mb-1 block text-xs font-medium text-slate-600">Send from</label>
              <select className="w-full !text-sm !py-2" value={senderId ?? ""} onChange={e => setSenderId(e.target.value ? Number(e.target.value) : null)}>
                {!needsSender && <option value="">Template's own from-address{picked?.from_email ? ` (${picked.from_email})` : ""}</option>}
                {needsSender && senders.length === 0 && <option value="">No sender accounts for {business?.name || "this business"} yet</option>}
                {senders.map(s => (
                  <option key={s.id} value={s.id}>{s.label} — {s.from_name ? `${s.from_name} <${s.from_email}>` : s.from_email}</option>
                ))}
              </select>
              {senders.length === 0 && (
                <p className="mt-1 text-[11px] text-slate-500">Add more sending addresses under <a className="underline" href="/outreach/senders">Outreach → Senders</a>.</p>
              )}
            </div>

            {picked && (
              <div className="mt-3 rounded-md bg-slate-50 p-3 text-xs text-slate-600">
                <strong className="text-slate-800">Preview:</strong> Will send "{picked.name}" to {sendable.length.toLocaleString()} contact(s)
                from <strong>{sender ? sender.from_email : picked.from_email || "the default address"}</strong>.
              </div>
            )}
            {unverified > 0 && (
              <div className="mt-2 rounded-md bg-amber-50 p-3 text-xs text-amber-800">
                {unverified} of {sendable.length} recipient(s) are not verified <strong>valid</strong>. Bounces from guessed addresses hurt the sender's reputation.
              </div>
            )}

            <div className="mt-4 flex items-end justify-between gap-2">
              <button className="btn-secondary !text-xs !py-1.5 !px-3" onClick={onClose}>Cancel</button>
              <SendScheduleControls
                sendNowLabel={`Send to ${sendable.length}`}
                scheduleLabel={`Schedule ${sendable.length}`}
                busy={busy}
                disabled={!pickedId || sendable.length === 0 || (needsSender && !senderId)}
                onSend={send}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
